import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  exportAuditLogsCsv,
  getAuditLogById,
  getAuditLogs,
} from '@/api/services/audit.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { usePermittedQuery } from '@/hooks/usePermittedQuery'
import type { AuditLogQueryParams } from '@/types'

export const auditLogsQueryKeys = {
  all: ['audit-logs'] as const,
  list: (params: AuditLogQueryParams | undefined) =>
    ['audit-logs', 'list', params] as const,
  detail: (id: string) => ['audit-logs', 'detail', id] as const,
}

export function useAuditLogs(params: AuditLogQueryParams | undefined) {
  return usePermittedQuery({
    module: 'audit',
    action: 'read',
    queryKey: auditLogsQueryKeys.list(params),
    queryFn: () => getAuditLogs(params),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useAuditLog(id: string | null) {
  return usePermittedQuery({
    module: 'audit',
    action: 'read',
    queryKey: auditLogsQueryKeys.detail(id ?? ''),
    queryFn: () => getAuditLogById(id!),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useExportAuditLogsCsv() {
  return useMutation({
    mutationFn: (params: AuditLogQueryParams | undefined) =>
      exportAuditLogsCsv(params),
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast.success('Export downloaded')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}
